import 'whatwg-fetch';
import { EntitlementError, ForbiddenError, type SchemaValidationErrorItem, UnauthorizedError } from '@takeshape/errors';
import { assert, PERSISTED_QUERY_NOT_FOUND_CODE, type PersistedQueryExtension } from '@takeshape/util';
import { DEFAULT_BRANCH } from 'app/branches/constants';
import type { TadaDocumentNode } from 'gql.tada';
import { type GraphQLError, print } from 'graphql';
import { getAdminEndpoint, getProjectEndpoint } from './branches/endpoints.js';
import { getToken } from './common/utils/get-token.js';
import { createSha256HashForPersistedQuery } from './util/crypto.js';

export type LegacyGraphQLError = GraphQLError & {
  /**
   * @deprecated Use `extensions.code` instead
   */
  type: string;
  /**
   * @deprecated Use `extensions.validationErrors` instead
   */
  validationErrors?: SchemaValidationErrorItem[];
};

export type WrappedGraphQLErrorOptions = ErrorOptions & {
  rawError?: LegacyGraphQLError[];
  rawGraphQLData?: GraphQLData;
};

export class WrappedGraphQLError extends Error {
  rawError: LegacyGraphQLError[];
  rawGraphQLData: GraphQLData;

  constructor(message: string, options: WrappedGraphQLErrorOptions) {
    super(`GraphQL Error: ${message}`);
    this.rawError = options.rawError ?? [];
    this.rawGraphQLData = options.rawGraphQLData ?? {};
  }
}

export type GraphQLErrorWithCode = LegacyGraphQLError & { extensions: { code: string } };

export type GraphQLClientError = ForbiddenError | EntitlementError | Error | WrappedGraphQLError;

export const isWrappedGraphQLError = (error: unknown): error is WrappedGraphQLError => {
  return error instanceof Error && Boolean((error as WrappedGraphQLError).rawError);
};

export const isGraphQLErrorWithCode = (error: LegacyGraphQLError): error is GraphQLErrorWithCode => {
  return typeof error.extensions?.code === 'string';
};

export const getGraphQLErrorMessage = (errors: LegacyGraphQLError[]) => {
  return errors[0].message;
};

export const getGraphQLErrorCode = (errors: LegacyGraphQLError[]) => {
  const errorWithCode = errors.find(isGraphQLErrorWithCode);

  if (!errorWithCode) {
    return;
  }

  return errorWithCode.extensions.code;
};

function formatValidationErrorMessage(validationErrors: SchemaValidationErrorItem[]) {
  const lineBreak = '\n\n';
  return (
    lineBreak +
    validationErrors.map((err: SchemaValidationErrorItem) => `[${err.path.join('.')}] ${err.message}`).join(lineBreak)
  );
}

// The default error handler
export function handleErrors(errors: LegacyGraphQLError[], data?: GraphQLData): void {
  const firstError = errors[0];

  if (firstError.extensions?.code === 'AuthorizationError') {
    throw new ForbiddenError('Not authorized.');
  }

  if (firstError.extensions?.code === 'EntitlementError') {
    throw new EntitlementError('Insufficient entitlements.');
  }

  if (firstError.validationErrors) {
    throw new Error(
      formatValidationErrorMessage(firstError.validationErrors ?? firstError.extensions.validationErrors)
    );
  }

  const { message } = firstError;

  throw new WrappedGraphQLError(message, { rawError: errors, rawGraphQLData: data });
}

async function doFetch(url: URL, options: RequestInit): Promise<{ data: GraphQLData; errors: LegacyGraphQLError[] }> {
  const response = await fetch(url, options);
  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  if (response.status !== 200) {
    throw new Error(`HTTP Status ${response.status}`);
  }

  return response.json();
}

function createOptions(method: string, token: string): RequestInit {
  return {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-TakeShape-Token': token,
      'X-TakeShape-Cache': 'false'
    }
  };
}

export interface GraphQLData {
  [name: string]: any;
}

export interface GraphQLVariables {
  [name: string]: any;
}

interface GraphQLRequestOptions {
  endpoint: string;
  query: string;
  variables?: GraphQLVariables;
  operationName?: string;
  isMutation: boolean;
}

function disablePersistedQueries(): boolean {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('disablePersistedQueries');
  } catch {
    return false;
  }
}

async function graphqlRequest(options: GraphQLRequestOptions): Promise<GraphQLData> {
  const { endpoint, query, variables, operationName, isMutation } = options;

  const token = getToken();
  if (!token) {
    throw new Error('Please log in to access this page.');
  }

  let data: GraphQLData | undefined;
  let errors: LegacyGraphQLError[] | undefined;

  const extensions: PersistedQueryExtension = {
    persistedQuery: {
      version: 1,
      sha256Hash: await createSha256HashForPersistedQuery(query, globalThis)
    }
  };

  const makePostRequest = async (includeExtensions: boolean) => {
    const requestOptions = createOptions('post', token);
    const url = new URL(endpoint);
    const body: any = {
      query,
      variables,
      operationName
    };
    if (includeExtensions) {
      body.extensions = extensions;
    }
    requestOptions.body = JSON.stringify(body);
    return doFetch(url, requestOptions);
  };

  // If persisted queries are disabled, just use the full query
  if (disablePersistedQueries()) {
    const response = await makePostRequest(false);
    data = response.data;
    errors = response.errors;
  } else {
    // With persisted queries, make an initial GET request using a cached query
    if (!isMutation) {
      const url = new URL(endpoint);
      const requestOptions = createOptions('get', token);
      url.searchParams.append('extensions', JSON.stringify(extensions));

      if (variables) {
        url.searchParams.append('variables', JSON.stringify(variables));
      }

      if (operationName) {
        url.searchParams.append('operationName', operationName);
      }

      const result = await doFetch(url, requestOptions);
      data = result.data;
      errors = result.errors;
    }

    // If the persisted query was not found (or it's a mutation), make a POST request (including the query hash)
    if (isMutation || (errors?.length === 1 && errors[0].extensions?.code === PERSISTED_QUERY_NOT_FOUND_CODE)) {
      const response = await makePostRequest(true);
      data = response.data;
      errors = response.errors;
    }
  }

  if (errors) {
    handleErrors(errors, data);
  }

  assert(data, 'No data');

  return data;
}

let projectEndpoint: string | undefined;
let adminEndpoint: string | undefined;

export function setProjectEndpoint(projectId?: string, branch: string = DEFAULT_BRANCH, hash?: string): void {
  if (projectId) {
    projectEndpoint = getProjectEndpoint(projectId, branch, branch === 'scratch' ? undefined : hash);
  } else {
    projectEndpoint = undefined;
  }

  adminEndpoint = getAdminEndpoint(projectId);
}

type PayloadFilter = (GraphQLData: GraphQLData | undefined) => GraphQLData | undefined;

const identity: PayloadFilter = (x) => x;

function createGraphQLFunction(getEndpoint: () => string | undefined, isMutation: boolean) {
  return <T = GraphQLData>(query: string, payloadFilter: PayloadFilter = identity) =>
    async (variables?: GraphQLVariables) => {
      const endpoint = getEndpoint();
      if (!endpoint) {
        throw new Error('Missing GraphQL API endpoint');
      }
      return graphqlRequest({
        endpoint,
        query,
        variables: payloadFilter(variables),
        isMutation
      }) as Promise<T>;
    };
}

function createAdminGraphQLFunction(isMutation: boolean) {
  const urlParams = new URLSearchParams(window.location.search);
  const disablePersistedQueries = urlParams.has('disablePersistedQueries');

  return <Data, Variables>(doc: TadaDocumentNode<Data, Variables>) =>
    async (variables?: Variables) => {
      if (!adminEndpoint) {
        throw new Error('Missing Admin GraphQL API endpoint');
      }
      return graphqlRequest({
        endpoint: adminEndpoint,
        query: print(doc),
        variables: variables ?? {},
        isMutation
      }) as Promise<Data>;
    };
}

export const queryUser = createGraphQLFunction(() => projectEndpoint, false);
export const mutateUser = createGraphQLFunction(() => projectEndpoint, true);
export const queryAdmin = createGraphQLFunction(() => adminEndpoint, false);
export const mutateAdmin = createGraphQLFunction(() => adminEndpoint, true);
export const newQueryAdmin = createAdminGraphQLFunction(false);
export const newMutateAdmin = createAdminGraphQLFunction(true);
